import { Building, DeviceEvent, Theme, Credentials, EcsDevice, BuildingOrg, BuildingId, User, AugmentedBuilding, Schedule, DeviceId } from 'shared/models';

export const Actions = {
    CREATE_BUILDING_SUCCESS: 'CREATE_BUILDING_SUCCESS',

    GET_BUILDINGS_REQUEST: 'GET_BUILDINGS_REQUEST',
    GET_BUILDINGS_SUCCESS: 'GET_BUILDINGS_SUCCESS',
    GET_BUILDINGS_FAILURE: 'GET_BUILDINGS_FAILURE',

    GET_BUILDING_THEMES_REQUEST: 'GET_BUILDING_THEMES_REQUEST',
    GET_BUILDING_THEMES_SUCCESS: 'GET_BUILDING_THEMES_SUCCESS',
    GET_BUILDING_THEMES_FAILURE: 'GET_BUILDING_THEMES_FAILURE',

    GET_BUILDING_REQUEST: 'GET_BUILDING_REQUEST',
    GET_BUILDING_SUCCESS: 'GET_BUILDING_SUCCESS',
    GET_BUILDING_FAILURE: 'GET_BUILDING_FAILURE',

    BUILDING_FETCHED: 'BUILDING_FETCHED',

    UPDATE_BUILDING_SUCCESS: 'UPDATE_BUILDING_SUCCESS',

    DELETE_BUILDING_REQUEST: 'DELETE_BUILDING_REQUEST',
    DELETE_BUILDING_SUCCESS: 'DELETE_BUILDING_SUCCESS',
    DELETE_BUILDING_FAILURE: 'DELETE_BUILDING_FAILURE',

    GET_EVENT_LOG_REQUEST: 'GET_EVENT_LOG_REQUEST',
    GET_EVENT_LOG_SUCCESS: 'GET_EVENT_LOG_SUCCESS',
    GET_EVENT_LOG_FAILURE: 'GET_EVENT_LOG_FAILURE',

    SET_CREDENTIALS_REQUEST: 'SET_CREDENTIALS_REQUEST',
    SET_CREDENTIALS_SUCCESS: 'SET_CREDENTIALS_SUCCESS',
    SET_CREDENTIALS_FAILURE: 'SET_CREDENTIALS_FAILURE',

    NEW_CREDENTIALS: 'NEW_CREDENTIALS',
    CHANGE_CREDENTIALS: 'CHANGE_CREDENTIALS',
    DELETE_CREDENTIALS: 'DELETE_CREDENTIALS',

    SET_BROADCAST_REQUEST: 'SET_BROADCAST_REQUEST',
    SET_BROADCAST_SUCCESS: 'SET_BROADCAST_SUCCESS',
    SET_BROADCAST_FAILURE: 'SET_BROADCAST_FAILURE',
};


export const createBuildingSuccess = (payload: { building: Building; buildingOrg: BuildingOrg | null }) => ({
    type: Actions.CREATE_BUILDING_SUCCESS,
    payload: payload,
});

export type GetBuildingsParams = {
    searchText?: string;
    role?: string;
    sortCriteria?: string;
    sortDirection?: string;
    maxRows?: number;
    page?: number;
    continueBuildingSearch?: (buildings: Building[]) => boolean;
};

export const getBuildingsRequest = (params: GetBuildingsParams = {}) => ({
    type: Actions.GET_BUILDINGS_REQUEST,
    payload: params,
});
export const getBuildingsSuccess = (payload: { buildings: Building[]; buildingOrgs: Record<BuildingId, BuildingOrg[]>; more: boolean }, params: GetBuildingsParams) => ({
    type: Actions.GET_BUILDINGS_SUCCESS,
    payload,
    meta: params,
});
export const getBuildingsFailure = (err: Error) => ({
    type: Actions.GET_BUILDINGS_FAILURE,
    error: true,
    payload: err,
});

//
// Used when a portion of a building is loaded outside of Redux-Saga and we need to
// store some building and device info in Redux so other parts of the site can work.
// For instance, this is used by the external elevator ID process for connecting to
// an elevator video call... the video monitor user will land on a special page that
// loads the call details (including basic building and device info) and jumps into
// a call without loading anything else. But the video call page relies on data from
// Redux to display the building address and device name.
//
export const buildingFetched = (payload: { building: Building; buildingOrgs: BuildingOrg[]; devices: EcsDevice[] }) => ({
    type: Actions.BUILDING_FETCHED,
    payload,
});

export const getBuildingThemesRequest = (buildingId: string) => ({
    type: Actions.GET_BUILDING_THEMES_REQUEST,
    payload: buildingId,
});
export const getBuildingThemesSuccess = ({ buildingId, themes }: { buildingId: string; themes: Theme[] }) => ({
    type: Actions.GET_BUILDING_THEMES_SUCCESS,
    payload: themes,
    meta: { buildingId },
});
export const getBuildingThemesFailure = (err: Error, buildingId: string) => ({
    type: Actions.GET_BUILDING_THEMES_FAILURE,
    payload: err,
    error: true,
    meta: { buildingId },
});

export const getBuildingRequest = (urlSlugOrId: string) => {
    if (!urlSlugOrId) {
        throw new Error('Missing url slug or building ID');
    }
    return {
        type: Actions.GET_BUILDING_REQUEST,
        payload: urlSlugOrId,
    };
};
type GetBuildingSuccessPayload = {
    building: AugmentedBuilding;
    buildingOrgs: BuildingOrg[];
    events: DeviceEvent[];
    schedules: Record<DeviceId, Schedule>;
    hasMoreEvents: boolean;
    credentials: unknown; // TODO
    broadcastMessage: string;
    themes: unknown; // TODO
    content: unknown; // TODO
    users: User[];
};
export const getBuildingSuccess = (payload: GetBuildingSuccessPayload, meta: { urlSlugOrId: string }) => ({
    type: Actions.GET_BUILDING_SUCCESS,
    payload,
    meta,
});
export const getBuildingFailure = (err: Error, meta: { urlSlugOrId: string }) => ({
    type: Actions.GET_BUILDING_FAILURE,
    payload: err,
    error: true,
    meta,
});

export const updateBuildingSuccess = (building: Building) => ({
    type: Actions.UPDATE_BUILDING_SUCCESS,
    payload: building,
    meta: { id: building.id },
});

export const deleteBuildingRequest = (buildingId: string) => ({
    type: Actions.DELETE_BUILDING_REQUEST,
    payload: buildingId,
});
export const deleteBuildingSuccess = (id: string) => ({
    type: Actions.DELETE_BUILDING_SUCCESS,
    payload: null,
    meta: { id },
});
export const deleteBuildingFailure = (err: Error, id: string) => ({
    type: Actions.DELETE_BUILDING_FAILURE,
    payload: err,
    error: true,
    meta: { id },
});

export const getEventLogRequest = (params: { buildingId: string; deviceId?: string; next?: number; maxRows?: number }) => ({
    type: Actions.GET_EVENT_LOG_REQUEST,
    payload: params,
});
export const getEventLogSuccess = ({ events, hasMore, buildingId }: { events: DeviceEvent[]; hasMore: boolean; buildingId: string }) => ({
    type: Actions.GET_EVENT_LOG_SUCCESS,
    payload: { events, hasMore },
    meta: { buildingId },
});
export const getEventLogFailure = ({ err, buildingId }: { err: Error; buildingId: string }) => ({
    type: Actions.GET_EVENT_LOG_FAILURE,
    payload: err,
    error: true,
    meta: { buildingId },
});

export const setCredentialsRequest = (params: { buildingId: string; credentials: Credentials[] }) => ({
    type: Actions.SET_CREDENTIALS_REQUEST,
    payload: params,
});
export const setCredentialsSuccess = ({ buildingId, credentials }: { buildingId: string; credentials: Credentials[] }) => ({
    type: Actions.SET_CREDENTIALS_SUCCESS,
    payload: credentials,
    meta: { buildingId },
});
export const setCredentialsFailure = ({ buildingId, err }: { buildingId: string; err: Error }) => ({
    type: Actions.SET_CREDENTIALS_FAILURE,
    payload: err,
    error: true,
    meta: { buildingId },
});

export const newCredentials = (buildingId: string) => ({
    type: Actions.NEW_CREDENTIALS,
    meta: { buildingId },
});
export const changeCredentials = (buildingId: string, provider: string, credentials: Credentials) => ({
    type: Actions.CHANGE_CREDENTIALS,
    payload: credentials,
    meta: { provider, buildingId },
});
export const deleteCredentials = (buildingId: string, provider: string) => ({
    type: Actions.DELETE_CREDENTIALS,
    payload: provider,
    meta: { buildingId },
});

export const setBroadcastRequest = (message: string | null, buildingId: string) => ({
    type: Actions.SET_BROADCAST_REQUEST,
    payload: message,
    meta: {
        buildingId,
    },
});
export const setBroadcastSuccess = (buildingId: string) => ({
    type: Actions.SET_BROADCAST_SUCCESS,
    meta: {
        buildingId,
    },
});
export const setBroadcastFailure = (err: Error, buildingId: string) => ({
    type: Actions.SET_BROADCAST_FAILURE,
    error: true,
    payload: err,
    meta: {
        buildingId,
    },
});
